import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const Field = ({ label, testId, ...props }) => (
  <label className="block">
    <span className="eyebrow block mb-1.5">{label}</span>
    <input data-testid={testId} {...props} className="w-full bg-[#0f0d11] border border-[#352d40] px-3 py-2.5 text-sm outline-none focus:border-[#D99B43] transition-colors" />
  </label>
);

const googleLogin = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/library";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Login() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { user, setUser, startCheckout } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const wantsUpgrade = params.get("next") === "upgrade";

  useEffect(() => {
    if (!user) return;
    if (wantsUpgrade && user.plan !== "pro") startCheckout(params.get("interval") === "year" ? "year" : "month");
    else nav("/library");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      setUser(await (mode === "login" ? api.login(form) : api.register(form)));
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#080708]" data-testid="login-page">
      <div className="w-full max-w-md stone-panel arch-top p-8 rise tube-glow">
        <Link to="/" className="font-gothic text-lg tracking-[0.15em] text-amber-100/90">VOX <span className="text-[#c92a3e]">✦</span> NOCTURNA</Link>
        <h1 className="font-gothic text-2xl uppercase tracking-wider text-stone-100 mt-6">{mode === "login" ? "Enter the sanctum" : "Join the choir"}</h1>
        {wantsUpgrade && <p className="font-cormorant italic text-stone-400 text-lg mt-1" data-testid="login-upgrade-note">Sign in to continue to Pro checkout.</p>}

        <button data-testid="google-login-btn" onClick={googleLogin} className="btn-gothic w-full justify-center mt-6">
          <svg width="14" height="14" viewBox="0 0 48 48" aria-hidden><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 38.2 44 33 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
          Continue with Google
        </button>
        <div className="flex items-center gap-3 my-6">
          <span className="flex-1 h-px bg-[#352d40]" />
          <span className="eyebrow">or with email</span>
          <span className="flex-1 h-px bg-[#352d40]" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && <Field label="Name" testId="auth-name-input" value={form.name} onChange={set("name")} placeholder="Your stage name" />}
          <Field label="Email" testId="auth-email-input" type="email" required value={form.email} onChange={set("email")} />
          <Field label="Password" testId="auth-password-input" type="password" required minLength={6} value={form.password} onChange={set("password")} />
          {error && <p className="text-sm text-[#E11D48]" data-testid="auth-error">{error}</p>}
          <button data-testid="auth-submit-btn" disabled={busy} className="btn-crimson w-full justify-center">
            {busy && <Loader2 size={14} className="animate-spin" />} {mode === "login" ? "Sign in" : "Create free account"}
          </button>
        </form>
        <button data-testid="auth-mode-toggle" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }} className="mt-5 text-sm text-stone-400 hover:text-amber-200 transition-colors">
          {mode === "login" ? "New here? Create a free account" : "Already have an account? Sign in"}
        </button>
      </div>
    </div>
  );
}
